/**
 * <ry-theme-panel>
 *
 * Floating theme/mode selector panel.
 * Theme and mode are orthogonal — every theme supports light and dark.
 *
 * Usage:
 * <ry-theme-panel></ry-theme-panel>
 *
 * Themes: "none" (bare structure), "default", "ocean"
 * Modes: "auto" (OS preference), "light", "dark"
 *
 * Sets data-ry-theme and data-ry-mode on <html>.
 * Persists selection in localStorage.
 */

import { RyElement } from '../core/ry-element.js';

interface ThemePanelChangeDetail {
  theme: string;
  mode: string;
}

const STORAGE_KEY = 'ry-theme-panel';

export class RyThemePanel extends RyElement {
  #theme = 'default';
  #mode = 'auto';

  setup(): void {
    // Restore from localStorage, then fall back to document attributes
    const stored = this.#loadStorage();
    this.#theme = stored?.theme
      ?? this.getAttribute('theme')
      ?? document.documentElement.dataset.ryTheme
      ?? 'default';
    this.#mode = stored?.mode
      ?? this.getAttribute('mode')
      ?? document.documentElement.dataset.ryMode
      ?? 'auto';

    // Normalize legacy "light"/"dark" theme values to mode
    if (this.#theme === 'light' || this.#theme === 'dark') {
      this.#mode = this.#theme;
      this.#theme = 'default';
    }

    this.innerHTML = this.#render();
    this.#bindButtons();
    this.#apply();
  }

  #bindButtons(): void {
    this.$$<HTMLButtonElement>('[data-ry-target="theme-btn"]').forEach(btn => {
      this.on(btn, 'click', () => {
        this.#theme = btn.dataset.value ?? 'default';
        this.#apply();
        this.#updatePressed();
        this.#save();
        this.emit<ThemePanelChangeDetail>('theme-panel-change', {
          theme: this.#theme, mode: this.#mode
        });
      });
    });

    this.$$<HTMLButtonElement>('[data-ry-target="mode-btn"]').forEach(btn => {
      this.on(btn, 'click', () => {
        this.#mode = btn.dataset.value ?? 'auto';
        this.#apply();
        this.#updatePressed();
        this.#save();
        this.emit<ThemePanelChangeDetail>('theme-panel-change', {
          theme: this.#theme, mode: this.#mode
        });
      });
    });
  }

  #apply(): void {
    const root = document.documentElement;

    // Theme attribute
    if (this.#theme === 'default') {
      delete root.dataset.ryTheme;
    } else {
      root.dataset.ryTheme = this.#theme;
    }

    // Mode: set color-scheme directly via inline style (most reliable)
    if (this.#mode === 'auto') {
      root.style.removeProperty('color-scheme');
    } else {
      root.style.colorScheme = this.#mode;
    }

    // Load theme-specific stylesheet (e.g. ocean.css)
    this.#loadThemeStylesheet();
  }

  #loadThemeStylesheet(): void {
    // Remove any previously loaded theme stylesheets by this component
    document.querySelectorAll('link[data-ry-theme-panel]').forEach(el => el.remove());

    if (this.#theme === 'ocean') {
      // Don't double-load if already present
      if (document.querySelector('link[href*="ocean.css"]')) return;

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = this.#resolveThemePath('ocean.css');
      link.setAttribute('data-ry-theme-panel', '');
      document.head.appendChild(link);
    }
  }

  #resolveThemePath(filename: string): string {
    // Check for base-path attribute override
    const basePath = this.getAttribute('base-path');
    if (basePath) return `${basePath}/${filename}`;

    // Try to find path from existing ry-ui or theme links
    const existingLink = document.querySelector('link[href*="ry-"]');
    if (existingLink) {
      const href = existingLink.getAttribute('href') ?? '';
      const base = href.substring(0, href.lastIndexOf('/'));
      return `${base}/../themes/${filename}`;
    }

    // Dev fallback
    return `/src/themes/${filename}`;
  }

  #updatePressed(): void {
    this.$$('[data-ry-target="theme-btn"]').forEach(btn => {
      const el = btn as HTMLElement;
      el.setAttribute('aria-pressed', String(el.dataset.value === this.#theme));
    });
    this.$$('[data-ry-target="mode-btn"]').forEach(btn => {
      const el = btn as HTMLElement;
      el.setAttribute('aria-pressed', String(el.dataset.value === this.#mode));
    });
  }

  #save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        theme: this.#theme, mode: this.#mode
      }));
    } catch { /* storage unavailable */ }
  }

  #loadStorage(): { theme: string; mode: string } | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as { theme: string; mode: string };
    } catch { return null; }
  }

  #render(): string {
    return `
      <div data-ry-target="panel" class="ry-theme-panel">
        <div data-ry-target="theme-row" class="ry-theme-panel__row">
          <span data-ry-target="label" class="ry-theme-panel__label">Theme</span>
          <div data-ry-target="theme-options" class="ry-theme-panel__options">
            ${this.#btn('theme-btn', 'none', 'None')}
            ${this.#btn('theme-btn', 'default', 'Default')}
            ${this.#btn('theme-btn', 'ocean', 'Ocean')}
          </div>
        </div>
        <div data-ry-target="mode-row" class="ry-theme-panel__row">
          <span data-ry-target="label" class="ry-theme-panel__label">Mode</span>
          <div data-ry-target="mode-options" class="ry-theme-panel__options">
            ${this.#btn('mode-btn', 'auto', 'Auto')}
            ${this.#btn('mode-btn', 'light', '&#9728;')}
            ${this.#btn('mode-btn', 'dark', '&#9790;')}
          </div>
        </div>
      </div>`;
  }

  #btn(target: string, value: string, label: string): string {
    const current = target === 'theme-btn' ? this.#theme : this.#mode;
    return `<button data-ry-target="${target}" data-value="${value}" class="ry-theme-panel__btn" aria-pressed="${value === current}">${label}</button>`;
  }

  // Public API
  get theme(): string { return this.#theme; }
  set theme(v: string) {
    this.#theme = v;
    this.#apply();
    this.#updatePressed();
    this.#save();
  }

  get mode(): string { return this.#mode; }
  set mode(v: string) {
    this.#mode = v;
    this.#apply();
    this.#updatePressed();
    this.#save();
  }
}

customElements.define('ry-theme-panel', RyThemePanel);
